import { CardType } from "@wingspanhq/wingspan-api-sdk";
import {
  useWSModal,
  WSActions,
  WSDivider,
  WSInfoBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { useState } from "react";
import { ErrorAlert } from "@components/ErrorAlert";
import { queriesCards } from "@query";
import { useQueryPrimaryComplianceEntity } from "../../../../shared/hooks/useQueryPrimaryComplianceEntity";
import { selectorCardShippingAddress } from "../../selectors/selectorCardShippingAddress";

export interface ReplaceCardProps {
  cardId: string;
  onSuccess: () => void;
  onClose: () => void;
}

const ReplaceCard: React.FC<ReplaceCardProps> = ({
  cardId,
  onClose,
  onSuccess
}) => {
  const queryPrimaryComplianceEntity = useQueryPrimaryComplianceEntity();
  const [error, setError] = useState<string>();
  const [createCard, createCardMeta] = queriesCards.createCard.useMutation();

  return (
    <WSList gap="L">
      <WSText.ParagraphSm mb="S">
        Would you like to replace an existing lost or damaged card?
      </WSText.ParagraphSm>
      <WSInfoBox>
        Your existing card will be closed and we will send you a replacement
        card with new numbers.
      </WSInfoBox>
      <ErrorAlert context="Other" error={error || createCardMeta.error} />
      <WSDivider />
      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Replace card",
            disabled: queryPrimaryComplianceEntity.isLoading,
            onAsyncClick: async () => {
              const physicalAddress =
                queryPrimaryComplianceEntity.data?.physicalAddress;

              if (!physicalAddress) {
                setError(
                  "Missing address. Please update your mailing address before requesting a replacement."
                );
                return;
              }

              const requestAddress =
                selectorCardShippingAddress(physicalAddress);

              if (!requestAddress) {
                setError(
                  "Invalid address. Please update your mailing address before requesting a replacement."
                );
                return;
              }

              setError(undefined);

              await createCard(
                {
                  cardCreate: {
                    cardType: CardType.PHYSICAL,
                    replacesCardId: cardId,
                    shippingAddress: requestAddress
                  }
                },
                {
                  onSuccess() {
                    onSuccess();
                    onClose();
                  }
                }
              );
            },
            kind: "Primary"
          },
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          }
        ]}
      ></WSActions>
    </WSList>
  );
};

export function useReplaceCardModal() {
  return useWSModal(ReplaceCard, {
    title: "Replace card",
    size: "S"
  });
}
